
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation, useNavigate } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
    Box,
    List,
    Collapse,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Tooltip
} from '@mui/material';
import storylaneContext from './Context/StoryLaneContext';

// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
    ({ theme }) => ({
        ...theme.typography.body2,
        height: 48,
        position: 'relative',
        textTransform: 'capitalize',
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(2.5),
        color: theme.palette.text.secondary,
        borderRadius: 8,
        '&:before': {
            top: 0,
            right: 0,
            width: 3,
            bottom: 0,
            content: "''",
            display: 'none',
            position: 'absolute',
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            backgroundColor: theme.palette.primary.main
        }
    })
);

const ListItemIconStyle = styled(ListItemIcon)({
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
});

// ----------------------------------------------------------------------

function NavItem({ item, active, sidebarCollapse }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isActiveRoot = active(item.path);
    const { title, path, icon, info, children, onClick } = item;

    const [open, setOpen] = useState(isActiveRoot);
    const { setVideoURL } = useContext(storylaneContext);
    const handleOpen = () => {
        setOpen((prev) => !prev);
    };

    const handleSubmenuClick = (e, childPath) => {
        setVideoURL(childPath);
    };
    const activeRootStyle = {
        color: 'text.primary',
        fontWeight: 'fontWeightMedium',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        '&:before': { display: 'block' }
    };

    const activeSubStyle = {
        color: theme.palette.primary.main,
        fontWeight: 'fontWeightMedium'
    };

    if (children) {
        return (
            <>
                <ListItemStyle
                    onClick={handleOpen}
                    sx={{
                        display: !sidebarCollapse && 'flex',
                        alignItems: 'center',
                        ...(isActiveRoot && activeRootStyle)
                    }}
                >
                    {sidebarCollapse ? (
                        <ListItemIconStyle>{icon}</ListItemIconStyle>
                    ) : (
                        <Tooltip title={title} followCursor placement="top">
                            <ListItemIconStyle>{icon}</ListItemIconStyle>
                        </Tooltip>
                    )}
                    {sidebarCollapse && <ListItemText disableTypography primary={title} />}
                    {sidebarCollapse && (
                        <Box
                            component={Icon}
                            icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                            sx={{ width: 16, height: 16, ml: 1, flexShrink: 0 }}
                        />
                    )}
                </ListItemStyle>

                <Collapse in={open} timeout={300} unmountOnExit>
                    <List component="div" disablePadding>
                        {children.map((child) => {
                            const isActiveSub = active(child.path);
                            return (
                                <ListItemStyle
                                    key={child.title}
                                    onClick={(e) => handleSubmenuClick(e, child.videoUrl)}
                                    component={RouterLink}
                                    to={child.path}
                                    sx={{
                                        pl: sidebarCollapse ? 5 : 8,
                                        ...(isActiveSub && activeSubStyle)
                                    }}
                                >
                                    {sidebarCollapse ? (
                                        <ListItemIconStyle>{child.icon}</ListItemIconStyle>
                                    ) : (
                                        <Tooltip title={child.title} followCursor placement="top">
                                            <ListItemIconStyle>{child.icon}</ListItemIconStyle>
                                        </Tooltip>
                                    )}
                                    {sidebarCollapse && <ListItemText disableTypography primary={child.title} />}
                                </ListItemStyle>
                            );
                        })}
                    </List>
                </Collapse>
            </>
        );
    }

    return (
        <ListItemStyle
            component={RouterLink}
            to={path}
            sx={{
                ...(isActiveRoot && activeRootStyle)
            }}
            onClick={onClick}
        >
            {sidebarCollapse ? (
                <ListItemIconStyle>{icon}</ListItemIconStyle>
            ) : (
                <Tooltip title={title} followCursor placement="top">
                    <ListItemIconStyle>{icon}</ListItemIconStyle>
                </Tooltip>
            )}
            {sidebarCollapse && <ListItemText disableTypography primary={title} />}
            {/* {info && info} */}
        </ListItemStyle>
    );
}

NavItem.propTypes = {
    item: PropTypes.object,
    active: PropTypes.func,
    sidebarCollapse: PropTypes.bool
};

export default function NavSection({ navConfig, sidebarCollapse, ...other }) {
    const { pathname } = useLocation();

    const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

    return (
        <Box
            sx={{
                display: !sidebarCollapse && 'flex',
                flexDirection: !sidebarCollapse && 'column',
                alignItems: !sidebarCollapse && 'center',
                justifyContent: !sidebarCollapse && 'center'
            }}
            {...other}
        >
            <List disablePadding>
                {navConfig.map((item) => (
                    <NavItem
                        key={item.title}
                        item={item}
                        active={match}
                        sidebarCollapse={sidebarCollapse}
                    />
                ))}
            </List>
        </Box>
    );
}

NavSection.propTypes = {
    navConfig: PropTypes.array,
    sidebarCollapse: PropTypes.bool
};
